import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';

import constants from '../../const.js';
import Modal from '../common/Modal';

const ModernButtonStyle = styled.button`
  border-radius: 6px;
  font-weight: 600;
  padding: 6px 12px;
  transition: all 0.3s ease;
  border: none;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-family: inherit;

  &.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &.btn-light {
    background: #ffffff;
    color: #667eea;
    border: 2px solid #667eea;

    &:hover:not(:disabled) {
      background: #667eea;
      color: white;
      transform: translateY(-2px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const ModernButton = (props) => {
  const { variant, onClick, disabled, children, style } = props;
  const className = variant ? `btn-${variant}` : '';
  
  return (
    <ModernButtonStyle
      className={className}
      onClick={onClick}
      disabled={disabled}
      style={style}
      type="button"
    >
      {children}
    </ModernButtonStyle>
  );
};

const PositionBadge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(135deg, #fff4e6 0%, #ffe0b3 100%);
  color: #333;
  border: 1px solid #ffc107;
  margin: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
  }
`;

const PositionGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  pointer-events: auto;
`;

const PositionCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #e0e0e0;
  background: white;
  pointer-events: auto;

  &:hover {
    border-color: #667eea;
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(102, 126, 234, 0.2);
  }

  &.selected {
    border-color: #667eea;
    background: linear-gradient(135deg, #f0f4ff 0%, #e3e9ff 100%);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .position-image {
    width: 100%;
    aspect-ratio: 125 / 83;
    object-fit: contain;
    margin-bottom: 6px;
    border-radius: 4px;
    background-color: #f5f5f5;
    pointer-events: none;
  }

  .position-info {
    text-align: center;
    width: 100%;
    pointer-events: none;
    
    .position-name {
      font-size: 12px;
      font-weight: 600;
      color: #333;
      margin-bottom: 2px;
      word-break: break-word;
    }

    .position-alias {
      font-size: 10px;
      color: #999;
    }
  }
`;

const SearchInputStyle = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 20px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  user-select: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .expand-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    transition: transform 0.3s ease;
    transform: ${props => props.expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
  }
`;

const PositionGridContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 12px;
  pointer-events: auto;
`;



function ImagePositionTagger(props) {
  console.log('ImagePositionTagger props:', props);
  const { imageKey, imageUrl, galleryFolderName, albumFolderName, onClose, onPositionsUpdate } = props;

  const [show, setShow] = useState(true);
  const [showPositionGrid, setShowPositionGrid] = useState(false);
  const [positions, setPositions] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [positionSearch, setPositionSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({ '2Main': true, '1Main': true });

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => (
      {
        ...prev,
        [categoryName]: !prev[categoryName]
      }
    ));
  };

  const API_URL = constants.DATA1_API_URL || 'http://localhost:5050';
  const token = localStorage.getItem('token');

  // Fetch existing positions for this image when modal opens
  useEffect(() => {
    if (!token || !imageKey || !galleryFolderName || !albumFolderName) {
      return;
    }

    const fetchExistingPositions = async () => {
      try {
        // Handle imageKey - may start with / or not
        let cleanImageKey = imageKey;
        if (cleanImageKey.startsWith('/')) {
          cleanImageKey = cleanImageKey.substring(1);
        }
        
        const filePath = `${galleryFolderName}/${albumFolderName}/${cleanImageKey}`;
        const encodedFilePath = encodeURIComponent(filePath);

        const response = await axios.get(
          `${API_URL}/gallery/image-positions/${encodedFilePath}`,
          {
            headers: {
              'Authorization': token
            }
          }
        );

        if (response.data && response.data.positions) {
          setPositions(response.data.positions);
          console.log(`✓ Loaded ${response.data.positions.length} existing positions for image`);
        }
      } catch (error) {
        // Silently fail if no existing positions
        if (error.response?.status !== 404) {
          console.error('Error fetching existing positions:', error.message);
        }
      }
    };

    fetchExistingPositions();
  }, [imageKey, galleryFolderName, albumFolderName, token, API_URL]);

  // Fetch all available positions
  useEffect(() => {
    if (!token) {
      console.warn('No token available for fetching positions');
      setLoading(false);
      return;
    }

    const fetchPositions = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/scenes/positions`,
          {
            headers: {
              'Authorization': token
            }
          }
        );
        if (response.data) {
          setAllPositions(response.data);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching positions:', error.message, error.response?.data);
        setLoading(false);
      }
    };

    fetchPositions();
  }, [API_URL, token]);

  const addPosition = () => {
    if (selectedPosition && !positions.some(p => p.id === selectedPosition.id)) {
      setPositions([...positions, selectedPosition]);
      setSelectedPosition(null);
    }
  };

  const removePosition = async (positionId) => {
    // Remove from local state immediately for responsive UI
    setPositions(positions.filter(p => p.id !== positionId));

    // If position exists in database, persist the deletion
    try {
      // Handle imageKey - may start with / or not
      let cleanImageKey = imageKey;
      if (cleanImageKey.startsWith('/')) {
        cleanImageKey = cleanImageKey.substring(1);
      }
      
      const filePath = `${galleryFolderName}/${albumFolderName}/${cleanImageKey}`;
      const encodedFilePath = encodeURIComponent(filePath);

      await axios.delete(
        `${API_URL}/gallery/image-positions/${encodedFilePath}/${positionId}`,
        {
          headers: {
            'Authorization': token
          }
        }
      );

      console.log(`✓ Removed position ${positionId} from image`);
    } catch (error) {
      // Silently fail if position doesn't exist in database
      if (error.response?.status !== 404) {
        console.error('Error removing position:', error.message);
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (onPositionsUpdate) {
        await onPositionsUpdate(imageKey, positions);
      }
      handleClose();
    } catch (error) {
      console.error('Error saving positions:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setShow(false);
    onClose && onClose();
  };

  return (<>
      {show && (
        <Modal
          title="Tag Positions for Image"
          onClose={handleClose}
          position="center"
          width="600px"
          zIndex={9998}
          moveLeft={showPositionGrid}
          footer={
            <div style={{ display: 'flex', gap: '8px', width: '100%', justifyContent: 'flex-end' }}>
              <ModernButton variant="light" onClick={handleClose} disabled={saving}>
                <CloseIcon /> Cancel
              </ModernButton>
              <ModernButton 
                variant="primary" 
                onClick={addPosition} 
                disabled={saving || !selectedPosition}
                title={!selectedPosition ? "Select a position first" : ""}
              >
                <AddIcon /> Add Position
              </ModernButton>
              <ModernButton 
                variant="primary" 
                onClick={handleSave} 
                disabled={saving || positions.length === 0}
              >
                {saving ? '⏳ Saving...' : 'Save Positions'}
              </ModernButton>
            </div>
          }
        >
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                width: '100%',
                maxWidth: '90%',
                borderRadius: '8px',
                marginBottom: '12px',
                backgroundColor: '#f0f0f0',
                margin: '0 auto 12px auto'
              }}
            >
              <img
                src={imageUrl}
                alt="Selected"
                style={{
                  width: 'auto',
                  height: '500px',
                  display: 'block'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                Select Position:
              </label>
              {selectedPosition ? (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f0f4ff', 
                  borderRadius: '6px', 
                  border: '1px solid #667eea',
                  marginBottom: '8px'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#333', marginBottom: '6px' }}>
                    Selected: {selectedPosition.name} {selectedPosition.alias && `(${selectedPosition.alias})`}
                  </div>
                  <ModernButton
                    variant="light"
                    onClick={() => setSelectedPosition(null)}
                    style={{ fontSize: '11px', padding: '4px 8px' }}
                  >
                    Clear Selection
                  </ModernButton>
                </div>
              ) : (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '6px', 
                  border: '1px dashed #ccc',
                  color: '#999',
                  fontSize: '12px'
                }}>
                  No position selected
                </div>
              )}
              <ModernButton
                variant="primary"
                onClick={() => setShowPositionGrid(true)}
                style={{ width: '100%', marginTop: '8px' }}
              >
                Browse Positions →
              </ModernButton>
            </div>

            <div>
              <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                Tagged Positions ({positions.length}):
              </label>
              <div style={{ minHeight: '40px', padding: '8px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                {positions.length > 0 ? (
                  positions.map(position => (
                    <PositionBadge key={position.id}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                        <span>
                          {position.name}
                          {position.alias && <span style={{ fontSize: '11px', opacity: 0.8 }}> • {position.alias}</span>}
                        </span>
                        <DeleteIcon
                          style={{
                            cursor: 'pointer',
                            fontSize: '14px',
                            marginLeft: '4px',
                            flexShrink: 0
                          }}
                          onClick={() => removePosition(position.id)}
                        />
                      </div>
                    </PositionBadge>
                  ))
                ) : (
                  <div style={{ color: '#999', fontSize: '12px' }}>No positions tagged yet</div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Position Grid Modal - appears to the right */}
      {showPositionGrid && (
        <Modal
          title="Select Position"
          onClose={() => {
            setShowPositionGrid(false);
            setPositionSearch('');
          }}
          position="right"
          width="800px"
          zIndex={9999}
        >
          <div style={{ marginBottom: '16px' }}>
            <SearchInputStyle
              type="text"
              placeholder="Search positions by name or alias..."
              value={positionSearch}
              onChange={(e) => setPositionSearch(e.target.value)}
            />
          </div>
          
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
              Loading positions...
            </div>
          ) : allPositions.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#d32f2f', fontSize: '14px' }}>
              No positions available
            </div>
          ) : (() => {
            const filteredPositions = allPositions.filter(pos =>
              (pos.category.name === '1Main' || pos.category.name === '2Main') &&
              (pos.name.toLowerCase().includes(positionSearch.toLowerCase()) ||
               (pos.alias && pos.alias.toLowerCase().includes(positionSearch.toLowerCase())))
            ).sort((a, b) => {
              // First, sort by category (2Main first, then 1Main)
              const categoryOrder = { '2Main': 0, '1Main': 1 };
              const catCompare = (categoryOrder[a.category.name] || 999) - (categoryOrder[b.category.name] || 999);
              if (catCompare !== 0) return catCompare;
              
              // Then sort by order attribute
              return (a.order || 0) - (b.order || 0);
            });

            if (filteredPositions.length === 0) {
              return (
                <div style={{ padding: '24px', textAlign: 'center', color: '#d32f2f', fontSize: '14px' }}>
                  No positions matching "{positionSearch}"
                </div>
              );
            }

            // Group positions by category
            const groupedByCategory = {};
            filteredPositions.forEach(pos => {
              const catName = pos.category.name;
              if (!groupedByCategory[catName]) {
                groupedByCategory[catName] = [];
              }
              groupedByCategory[catName].push(pos);
            });

            // Render categories in order
            const categories = ['2Main', '1Main'];
            return (
              <div>
                {categories.map(catName => (
                  groupedByCategory[catName] && groupedByCategory[catName].length > 0 && (
                    <CategorySection key={catName}>
                      <CategoryHeader 
                        expanded={expandedCategories[catName]} 
                        onClick={() => toggleCategory(catName)}
                      >
                        <span className="expand-icon">›</span>
                        <span>{catName} ({groupedByCategory[catName].length})</span>
                      </CategoryHeader>
                      {expandedCategories[catName] && (
                        <PositionGridContent>
                          {groupedByCategory[catName].map(pos => (
                            <PositionCard
                              key={pos.id}
                              className={selectedPosition?.id === pos.id ? 'selected' : ''}
                              onClick={() => {
                                setSelectedPosition(pos);
                                setShowPositionGrid(false);
                              }}
                              title={`${pos.name}${pos.alias ? ' - ' + pos.alias : ''}`}
                            >
                              <img
                                src={`${constants.CONTENT_DOMAIN}download/sus-media/positions/${pos.id}.png?token=${localStorage.getItem('token')?.slice(7)}`}
                                alt={pos.name}
                                className="position-image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              <div className="position-info">
                                <div className="position-name">{pos.name}</div>
                                {pos.alias && <div className="position-alias">{pos.alias}</div>}
                              </div>
                            </PositionCard>
                          ))}
                        </PositionGridContent>
                      )}
                    </CategorySection>
                  )
                ))}
              </div>
            );
          })()}
        </Modal>
      )}</>
  );
}

export default ImagePositionTagger;
